import React, { FC, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { PackageSearchSelectListItem } from "src/components/PackageSearchSelectListItem";
import { measureTypeOptions } from "src/pages/App/pages/Organization/pages/Product/utils";
import { FormInput } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as PackageSVG } from "../../../images/svg/products/package.svg";
import {
	CreateAndCloseLinkedApi,
	LinkedApi,
	ProductApi,
	ProductCategoryApi,
	ProductComponentApi,
	TaxApi,
} from "../../../services/Api/Organization/types";
import { api } from "../../../services/Api/api";
import CreateEntityModalForm from "./CreateEntityModalForm";

interface Props {
	isShown: boolean;
	onHide: () => void;
	product: Partial<ProductApi>;
	onGoBack: () => void;
	linkedId: number;
	onSuccess: (response: LinkedApi | ProductApi) => void;
}

const CreateProductModalForm: FC<Props> = ({ isShown, onHide, product, onGoBack, linkedId, onSuccess }) => {
	const { t } = useTranslation();
	const form = useForm<CreateAndCloseLinkedApi>({
		criteriaMode: "all",
		defaultValues: {
			tax_id: product?.tax?.id,
			measure_type: "ITEM",
			name: product?.name,
			item_name: product?.name,
			sku: product?.sku,
			recipe_amount: 1,
			components: [{}],
			type: product?.type,
		},
	});
	const {
		formState: { errors },
		register,
		getValues,
		watch,
		control,
		setValue,
	} = form;
	const { remove, append } = useFieldArray({
		control,
		name: "components",
		keyName: "key",
	});
	const isPackage = product.type === "PACKAGE";
	const [tax, setTax] = useState<TaxApi | undefined>(product?.tax);
	const [category, setCategory] = useState<ProductCategoryApi | undefined>(product?.category);
	const watchedComponents = watch("components");
	const organization = useSelector(selectOrganization);
	const componentsAsString = JSON.stringify(watchedComponents);
	const currency = organization?.currency ?? "PLN";

	const checkIfAddNewComponent = () => {
		if (!watchedComponents) return false;
		const prevItem = watchedComponents[watchedComponents?.length - 1];
		return prevItem?.product_id || prevItem?.recipe_amount;
	};

	useEffect(() => {
		if (checkIfAddNewComponent()) {
			append({
				id: new Date().getTime(),
				product_id: null,
				recipe_amount: null,
				product_name: "",
				measure_type: "ITEM",
				product_cost_gross_money: null,
				product_cost_money: null,
			} as unknown as ProductComponentApi);
		}
	}, [componentsAsString]);

	const onSearchCategory = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductCategorySearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onSearchTax = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getTaxSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const onSearchProduct = async (t: string, obj: Record<string, any>, options?: Record<string, any>) => {
		return api.organization().getProductSearchSelect(t, obj, {
			cancelToken: options?.token,
		});
	};

	const mappedMeasureTypeOptions = measureTypeOptions.map((type) => ({
		value: type,
		label: t(`enums.products.measure_types.${type}`),
	}));

	const onChangeTax = async (tax: TaxApi) => {
		setTax(tax);
		setValue(`tax_id`, tax?.id, { shouldDirty: true });
	};

	const onChangeCategory = async (category: ProductCategoryApi) => {
		setCategory(category);
		setValue(`category_id`, category?.id, { shouldDirty: true });
	};

	const onRemoveComponent = (index: number) => remove(index);

	const getTotalCost = (index: number) => {
		const recipeAmount = watch(`components.${index}.recipe_amount`) || 0;
		const unitProductCostMoney = getValues(`components.${index}.unit_product_cost_money`);
		const unitCost = unitProductCostMoney
			? unitProductCostMoney.amount
			: getValues(`components.${index}.product_cost_money.amount`) || 0;

		const unitProductGrossCostMoney = getValues(`components.${index}.unit_product_cost_gross_money`);
		const grossCost = unitProductGrossCostMoney
			? unitProductGrossCostMoney.amount
			: getValues(`components.${index}.product_cost_gross_money.amount`) || 0;

		return {
			totalCost: recipeAmount * unitCost,
			totalGrossCost: recipeAmount * grossCost,
		};
	};

	const drawCost = (index: number) => {
		return `${FormatMoney({
			amount: getTotalCost(index).totalCost,
			currency,
		})} (${FormatMoney({ amount: getTotalCost(index).totalGrossCost, currency })})`;
	};

	const onChangeProduct = async (product: ProductApi, index: number) => {
		if (!product) return;

		setValue(`components.${index}.product_name`, product.name);
		setValue(`components.${index}.product_cost_gross_money`, product.cost_price_gross_money);
		setValue(`components.${index}.product_cost_money`, product.cost_price_money);
		setValue(`components.${index}.measure_type`, product.measure_type);
		setValue(`components.${index}.product_id`, product.id);
		setValue(`components.${index}.sub_product_type`, product.type);
	};

	const title = isPackage
		? t("modules.app_link.field.create_package.title")
		: t("modules.app_link.field.create_product.title");

	return (
		<CreateEntityModalForm
			title={title}
			onHide={onHide}
			isShown={isShown}
			onGoBack={onGoBack}
			resourceType="PRODUCT"
			form={form}
			linkedId={linkedId}
			onSuccess={onSuccess}
		>
			<div className="row">
				<div className="col-12">
					<FormInput
						name={linkedId ? "name" : "item_name"}
						errors={errors}
						register={register}
						label={t("lib:common.word.name")}
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-md-6">
					<FormInput
						name="sku"
						errors={errors}
						register={register}
						label={t("modules.app_link.field.sku.title")}
					/>
				</div>
				<div className="col-md-6">
					<FormSelectGroup
						errors={errors}
						label={t("common.word.category")}
						control={control}
						onChange={(id, obj) => onChangeCategory(obj)}
						getOptionValue={(opt) => opt.id}
						defaultValue={{
							label: category?.name,
							id: category?.id,
						}}
						name="category_id"
						loadOptions={onSearchCategory}
						data-testid="category_id"
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-md-6">
					<FormSelectGroup
						errors={errors}
						control={control}
						options={mappedMeasureTypeOptions}
						name="measure_type"
						label={t("common.word.measure_type")}
						data-testid="measure_type"
					/>
				</div>
				<div className="col-md-6">
					{isPackage ? (
						<FormNumberInput
							errors={errors}
							name="recipe_amount"
							control={control}
							suffix=""
							label={t("modules.app_link.field.recipe_amount.title")}
						/>
					) : (
						<FormSelectGroup
							errors={errors}
							control={control}
							loadOptions={onSearchTax}
							onChange={(id, obj) => onChangeTax(obj)}
							getOptionValue={(opt) => opt.id}
							name="tax_id"
							defaultValue={{
								label: tax?.name,
								id: tax?.id,
							}}
							label={t("modules.app_link.field.tax.title")}
							data-testid="tax_id"
						/>
					)}
				</div>
			</div>
			{isPackage && (
				<div className="my-3">
					<h5>{t("modules.app_link.field.recipe.title")}</h5>
					<table className="table table-form positions-table">
						<thead>
							<tr>
								<th className="action" />
								<th className="w-40">{t("lib:common.word.name")}</th>
								<th>{t("common.word.measure")}</th>
								<th>{t("common.word.quantity")}</th>
								<th>
									{t("common.word.cost")} ({t("modules.app_link.field.gross_cost.title")})
								</th>
							</tr>
						</thead>
						<tbody>
							{watchedComponents?.map((item, index) => (
								<tr key={`component-${index}`}>
									<td className="action" />
									<td>
										<>
											{item?.product_id ? (
												<div className="d-flex align-items-center">
													<Link
														target="_blank"
														to={`/${organization.id}/products/${item.product_id}/edit`}
													>
														{item?.product_name}
													</Link>
													{item?.sub_product_type === "PACKAGE" && (
														<PackageSVG className="ms-2" />
													)}
												</div>
											) : (
												<FormSelectGroup
													name={`components.${index}.product_name`}
													errors={errors}
													onChange={(id, product) => onChangeProduct(product, index)}
													getOptionLabel={(option) => {
														return option.product_type === "PACKAGE" ? (
															<PackageSearchSelectListItem label={option.label} />
														) : (
															option.label
														);
													}}
													defaultValue={{
														label: item?.product_name,
														id: item?.product_id,
													}}
													loadOptions={onSearchProduct}
													control={control}
													data-testid={`components.${index}.product_name`}
												/>
											)}
											<FormInput
												type="hidden"
												errors={errors}
												register={register}
												name={`components.${index}.product_id`}
												defaultValue={item?.product_id}
											/>
										</>
									</td>
									<td>
										{item?.measure_type && t(`enums.products.measure_types.${item.measure_type}`)}
									</td>
									<td>
										<FormNumberInput
											errors={errors}
											control={control}
											defaultValue={item.recipe_amount || null}
											decimalScale={5}
											name={`components.${index}.recipe_amount`}
											suffix=""
										/>
									</td>
									<td>{drawCost(index)}</td>
									<td className="action">
										{!(
											(watchedComponents.length === 1 && index === 0) ||
											index === watchedComponents.length - 1
										) && <RemoveSVG className="icon" onClick={() => onRemoveComponent(index)} />}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</CreateEntityModalForm>
	);
};

export default CreateProductModalForm;
